import { AccountFeatureFlag, hasFeatureFlag } from '@/services/auth/feature-flags';
import { AccountState, PlanFeature, type AugmentedAccount, type AugmentedUser } from '@/services/auth/types';
import type { TargetTransitionOptions } from '@/services/router/safe-navigate';
import { isEmptyString } from '@/utils/string';
import type { usePermissions } from '@customerio/hydra-permissions/react';
import type { IconName } from '@customerio/pluma-icons';
import { iconNames } from '@customerio/pluma-icons';
import type { MergedWorkspace, WorkspaceObjectType } from '../../../../features/workspaces';
import { AppBundle, AppBundleRoutes } from '../../../../utils/app-bundles';

export interface NavItemGroup {
  title?: string;
  items: NavItem[];
}

export interface NavItem {
  id: string;
  label: string;
  iconName?: IconName;
  href?: string;
  children?: NavItem[];
  showNewLabel?: boolean;
  showPremiumLabel?: boolean;
  activeWhenUrls?: string[];
  activeExceptUrls?: string[];
  transitionOptions?: TargetTransitionOptions;
  shouldShowHome?: boolean;
  variant?: 'verdant' | 'evergreen' | 'default';
  onSampleWorkspace?: NavItem;
  showIconOnExpand?: boolean;
  activateOnPipelinesRoutes?: string[];
}

function shouldShowFullJourneysRoutesMenu(auth: { account: AugmentedAccount | null; user: AugmentedUser | null }) {
  const { user, account } = auth;

  if (user == null || account == null) {
    return false;
  }

  // if masqueraded into an account
  if (account?.is_masq ?? false) {
    // if the user doesn't have all permissions properly set to masq return false
    if (user.noMasqAccess || user.masqLevelNotSet) {
      return false;
    }

    // if the user has masq permissions and accessGrantedUntil is set return true
    if (user.isSuperUser || user.canReadWriteMasq || user.canReadOnlyMasq) {
      return true;
    }

    return false;
  }

  return true;
}

export function generateRoutes(
  currentWorkspace: MergedWorkspace,
  auth: { account: AugmentedAccount | null; user: AugmentedUser | null },
  { hasAnyPermission, hasPermission }: ReturnType<typeof usePermissions>,
  extraConfig?: {
    shouldShowHome?: boolean;
    sampleWorkspaceId?: number | null;
    objectTypes?: WorkspaceObjectType[];
    isNewLayout?: boolean;
  },
): NavItemGroup[] {
  if (currentWorkspace.cio_workspace_id == null || currentWorkspace.cio_workspace_id === 0) {
    return [];
  }

  const isTrial = auth?.account?.account_state === AccountState.Free;

  const { shouldShowHome, objectTypes, sampleWorkspaceId } = extraConfig ?? {
    shouldShowHome: false,
    sampleWorkspaceId: null,
    objectTypes: [],
  };
  const envPrefix = `/workspaces/${currentWorkspace.id}`;
  const envAppPrefix = `${envPrefix}${AppBundleRoutes[AppBundle.Journeys]}`;

  const showFullMenu = shouldShowFullJourneysRoutesMenu(auth);
  const isAnonymousInAppEnabled = auth.account?.planFeatures[PlanFeature.AnonymousInApp].enabled ?? false;

  const isGoalsTrackingEnabled = hasFeatureFlag(AccountFeatureFlag.GoalsTracking);
  const isAiAssistantV2Enabled = hasFeatureFlag(AccountFeatureFlag.AiAssistantV2);

  if (!showFullMenu) {
    return [];
  }

  return (
    [
      {
        items: [
          isAiAssistantV2Enabled
            ? {
                id: 'agent',
                label: 'Agent',
                href: `${envPrefix}/agent`,
                iconName: 'sparkle',
              }
            : null,
          {
            id: 'dashboard',
            label: shouldShowHome === true ? 'Home' : 'Dashboard',
            href: `${envAppPrefix}/dashboard`,
            iconName: shouldShowHome === true ? 'home' : 'dashboard',
            activeWhenUrls: [`${envAppPrefix}/dashboard`, `${envAppPrefix}/home`],
          },
          {
            id: 'analysis',
            label: 'Analysis',
            href: `${envAppPrefix}/analysis`,
            iconName: 'analysis',
          },
          isGoalsTrackingEnabled
            ? {
                id: 'goals',
                label: 'Goals',
                href: `${envAppPrefix}/goals`,
                iconName: 'goal',
              }
            : null,
        ],
      },
      {
        items: [
          {
            id: 'campaigns',
            label: 'Campaigns',
            href: `${envAppPrefix}/campaigns`,
            iconName: 'campaigns',
          },
          {
            id: 'broadcasts',
            label: 'Broadcasts',
            href: `${envAppPrefix}/broadcasts`,
            iconName: 'broadcast',
          },
          {
            id: 'transactional',
            label: 'Transactional',
            href: `${envAppPrefix}/transactional`,
            iconName: 'transactional-api',
          },
          isAnonymousInAppEnabled
            ? {
                id: 'anonymous',
                label: 'Anonymous',
                href: `${envAppPrefix}/anonymous`,
                iconName: 'anonymous',
              }
            : null,
          {
            id: 'outbox',
            label: 'Deliveries & Drafts',
            href: `${envAppPrefix}/outbox`,
            iconName: 'delivery-log',
          },
        ],
      },
      {
        items: [
          {
            id: 'people',
            label: 'People',
            href: `${envAppPrefix}/people`,
            iconName: 'person',
          },
          ...generateObjectTypeRoutes(envAppPrefix, objectTypes ?? []),
          {
            id: 'segments',
            label: 'Segments',
            href: `${envAppPrefix}/segments`,
            iconName: 'segment',
          },
          {
            id: 'logs',
            label: 'Activity Logs',
            href: `${envAppPrefix}/activity_logs`,
            iconName: 'activity',
          },
          {
            id: 'data-integrations',
            label: 'Data & Integrations',
            iconName: 'database',
            children: [
              {
                id: 'data-index',
                label: 'Data Index',
                href: `${envAppPrefix}/data-index`,
              },
              hasAnyPermission('journeys/imports/view', 'journeys/exports/view') === true
                ? {
                    id: 'integrations',
                    label: 'Integrations',
                    href: `${envAppPrefix}/integrations`,
                    activateOnPipelinesRoutes: [AppBundleRoutes[AppBundle.Pipelines]],
                  }
                : null,
              hasPermission('journeys/imports/view') === true
                ? {
                    id: 'imports',
                    label: 'Imports',
                    href: `${envAppPrefix}/imports`,
                  }
                : null,
              hasPermission('journeys/exports/view') === true
                ? {
                    id: 'exports',
                    label: 'Exports',
                    href: `${envAppPrefix}/exports`,
                  }
                : null,
              {
                id: 'collections',
                label: 'Collections',
                href: `${envAppPrefix}/collections`,
                showPremiumLabel: auth.account?.planFeatures[PlanFeature.Collections].showPremiumLabel ?? true,
              },
            ],
          },
        ],
      },
      {
        items: [
          {
            id: 'content',
            label: 'Content',
            iconName: 'layout',
            children: [
              {
                id: 'message-library',
                label: 'Message Library',
                href: `${envAppPrefix}/message-library`,
              },
              {
                id: 'assets',
                label: 'Assets',
                href: `${envAppPrefix}/assets`,
              },
              {
                id: 'layouts',
                label: 'Email Layouts',
                href: `${envAppPrefix}/layouts`,
              },
              {
                id: 'snippets',
                label: 'Snippets',
                href: `${envAppPrefix}/snippets`,
              },
            ],
          },
          {
            id: 'design-studio',
            label: 'Design Studio',
            iconName: extraConfig?.isNewLayout === true ? 'design-studio-new' : 'design-studio',
            href: `${envPrefix}/design-studio/dashboard`,
          },
        ],
      },
      auth.account?.id === 1
        ? {
            items: [
              {
                id: 'internal-tools',
                label: 'Internal Tools',
                iconName: 'a-b-test',
                href: '/internal-tools',
              },
            ],
          }
        : { items: [] },
      // Invite Teammates should always be the last the in the nav
      // otherwise it will not render at the bottom of the nav
      isTrial
        ? {
            items: [
              {
                href: `/workspaces/${sampleWorkspaceId}/journeys`,
                iconName: 'trigger-event',
                id: 'view-workspace',
                label: 'View sample workspace',
                showIconOnExpand: false,
                variant: 'verdant',
                onSampleWorkspace: {
                  href: `/workspaces/${auth.user?.last_cio_workspace_id}/journeys`,
                  label: 'View my workspace',
                  showIconOnExpand: true,
                  variant: 'default',
                },
              },
              hasPermission('account/team_members/edit') === true
                ? {
                    href: '/settings/team',
                    iconName: 'send-auto',
                    id: 'invite-teammates',
                    label: 'Invite teammates',
                    variant: 'evergreen',
                  }
                : null,
            ],
          }
        : { items: [] },
    ] as NavItemGroup[]
  )
    .map((group) => {
      const updatedGroup: NavItemGroup = { title: group.title, items: [] };

      for (const item of group.items ?? []) {
        if (item == null) {
          continue;
        }

        if ('children' in item && item.children != null) {
          const children = item.children.filter((child) => child != null) as NavItem[];
          updatedGroup.items.push({ ...item, children } as NavItem);
        } else if (item != null) {
          updatedGroup.items.push(item as NavItem);
        }
      }

      return updatedGroup;
    })
    .filter((group) => group.items.length !== 0);
}

export function isNavItemActive(item: NavItem, currentUrl: string) {
  let isActive = false;

  if (item.activeWhenUrls != null) {
    isActive = item.activeWhenUrls.some((url) => currentUrl.startsWith(url));
  }

  if (!isActive && item.href != null) {
    isActive = currentUrl.startsWith(item.href);
  }

  if (isActive && item.activeExceptUrls != null) {
    isActive = !item.activeExceptUrls.some((url) => currentUrl.startsWith(url));
  }

  if (!isActive) {
    const pipelinesRoute = AppBundleRoutes[AppBundle.Pipelines];
    const isPipelinesRoute = pipelinesRoute != null && currentUrl.includes(pipelinesRoute);
    if (isPipelinesRoute && item.activateOnPipelinesRoutes != null && item.activateOnPipelinesRoutes.length > 0) {
      isActive = item.activateOnPipelinesRoutes.some((route) => route != null && currentUrl.includes(route));
    }
  }

  return isActive;
}

function generateObjectTypeRoutes(envAppPrefix: string, workspaceObjectTypes?: WorkspaceObjectType[]) {
  const objectTypes =
    workspaceObjectTypes?.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }

      if (a.name > b.name) {
        return 1;
      }

      return 0;
    }) ?? [];

  if (objectTypes.length === 0) {
    return [
      {
        id: 'objects',
        label: 'Custom Objects',
        href: `${envAppPrefix}/objects`,
        iconName: 'object',
      },
    ];
  }

  if (objectTypes.length <= 3) {
    return objectTypes.map((objectType) => {
      // Type guard: check if icon is a valid IconName
      const isValidIcon = (icon: string | undefined): icon is IconName => {
        return icon != null && !isEmptyString(icon.trim()) && (iconNames as readonly string[]).includes(icon);
      };

      const icon: IconName = isValidIcon(objectType.icon) ? objectType.icon : 'object';

      return {
        id: `object-types_${objectType.id}`,
        label: objectType.name,
        href: `${envAppPrefix}/objects/${objectType.slug}`,
        iconName: icon,
      };
    });
  }

  return [
    {
      id: 'custom-objects',
      label: 'Custom Objects',
      iconName: 'object',
      children: [
        ...objectTypes.map((objectType) => ({
          id: `object-types_${objectType.id}`,
          label: objectType.name,
          href: `${envAppPrefix}/objects/${objectType.slug}`,
        })),
      ],
    },
  ];
}
